import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, asList } from '../lib/api';

/* ============================================================================
   INVOICE DESIGNER

   The API for this has been deployed since the advanced-invoicing delta — create a
   template, add blocks, position them, style them, publish, preview. Ten endpoints,
   all working. What was missing was a page to drive them.

   This is that page: drag blocks around an A4 sheet and see the invoice update.

   ⚠ A school can move and restyle anything, and delete anything EXCEPT four blocks:
   the invoice number, the school identity, the totals, and the tax block when tax
   applies. Those are enforced in the domain, not here — this page only reflects the
   rule so a school is told before they try, rather than after.

   An invoice with no number cannot be reconciled. One with no issuer is not
   attributable. One charging GST without a GSTIN is not a valid tax invoice, and the
   school carries that with their auditor.
   ============================================================================ */

const PX_PER_MM = 96 / 25.4;

/**
 * ⚠ The page is rendered with `zoom: 0.62` so an A4 sheet fits beside the palette and the preview.
 *
 * `zoom` scales what the user SEES, but MouseEvent.clientX is still in real screen pixels. Dividing a
 * screen delta by PX_PER_MM alone would move a block 1/0.62 times too far — it runs away from the
 * cursor, and the further you drag the worse it gets.
 *
 * Read from the DOM rather than hardcoded, so changing the zoom in CSS cannot silently break
 * dragging — which is precisely the kind of thing that breaks six weeks later with no clue why.
 */
function pageZoom(el: HTMLElement | null): number {
  if (!el) return 1;
  const z = parseFloat(getComputedStyle(el).zoom || '1');
  return Number.isFinite(z) && z > 0 ? z : 1;
}
const PAGE_W = 210;
const PAGE_H = 297;

interface Block {
  id: string;
  kind: string;
  x: number; y: number; width: number; height: number;
  zOrder: number;
  text: string | null;
  fontFamily: string | null;
  fontSizePt: number;
  bold: boolean;
  align: string | null;
  colorHex: string | null;
  backgroundHex: string | null;
  borderHex: string | null;
  visible: boolean;
  isMandatory: boolean;
}

interface Template {
  id: string;
  name: string;
  isDefault: boolean;
  documentTitle: string | null;
  accentHex: string | null;
  blocks: Block[];
  problems: string[];
}

/** Grouped by what a school is trying to do, not by anything technical. */
const PALETTE: { group: string; items: { kind: string; label: string }[] }[] = [
  {
    group: 'Identity',
    items: [
      { kind: 'Logo', label: 'Logo' },
      { kind: 'SchoolIdentity', label: 'School name & address' },
      { kind: 'Signature', label: 'Signature line' },
    ],
  },
  {
    group: 'Invoice',
    items: [
      { kind: 'InvoiceMeta', label: 'Number & date' },
      { kind: 'StudentDetails', label: 'Student details' },
      { kind: 'LineItems', label: 'Fee items' },
      { kind: 'Totals', label: 'Totals' },
      { kind: 'TaxBlock', label: 'Tax summary' },
    ],
  },
  {
    group: 'Payment',
    items: [
      { kind: 'PaymentDetails', label: 'Payments received' },
      { kind: 'BankDetails', label: 'Bank details' },
      { kind: 'QrCode', label: 'Payment QR' },
    ],
  },
  {
    group: 'Other',
    items: [
      { kind: 'TermsText', label: 'Terms' },
      { kind: 'FreeText', label: 'Free text' },
      { kind: 'Divider', label: 'Divider' },
    ],
  },
];

/**
 * Colours a school can choose from.
 *
 * ⚠ A LIST, NOT A COLOUR PICKER.
 *
 * A free picker lets someone set pale yellow text on white, and the invoice looks fine on screen and
 * prints as a blank line. These are all legible on paper, which is where an invoice actually lives.
 *
 * "Use school colour" is first because it is what most schools want, and it follows the branding they
 * already set rather than being a fifth place to configure it.
 */
const COLOURS: { value: string | null; label: string }[] = [
  { value: null, label: 'Use school colour' },
  { value: '#1b1b18', label: 'Black' },
  { value: '#6b6b66', label: 'Grey' },
  { value: '#0e1a2b', label: 'Navy' },
  { value: '#8a6a2e', label: 'Gold' },
  { value: '#1a7f37', label: 'Green' },
  { value: '#b3261e', label: 'Red' },
];

/** Point sizes that make sense on an A4 document. 6pt is fine print; 22pt is a title. */
const FONT_SIZES = [6, 7, 8, 9, 10, 11, 12, 14, 16, 18, 22];

const FONTS: { value: string | null; label: string }[] = [
  { value: null, label: 'Default' },
  { value: "Georgia, 'Times New Roman', serif", label: 'Serif' },
  { value: "'Helvetica Neue', Arial, sans-serif", label: 'Sans serif' },
  { value: "'IBM Plex Mono', monospace", label: 'Monospace' },
];

const KIND_LABEL: Record<string, string> = Object.fromEntries(
  PALETTE.flatMap((g) => g.items.map((i) => [i.kind, i.label])),
);

export function InvoiceDesignerPage() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [template, setTemplate] = useState<Template | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);

  const pageRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ id: string; startX: number; startY: number; blockX: number; blockY: number } | null>(null);

  /**
   * ⚠ THE PREVIEW PANE WAS SHOWING THE TOP-LEFT CORNER OF THE INVOICE AND NOTHING ELSE.
   *
   * The frame holds an A4 page — 794 CSS pixels wide at 96dpi — inside a column about 260px wide.
   * Nothing scaled it, so a school arranging their own document could see the logo and the first
   * two fee lines, and never the totals, the terms or the signature they were positioning. It looks
   * identical on a 1280 laptop and a 1920 monitor, which is why it survived: the pane is a fixed
   * fraction of a three-column layout, so widening the browser does not help.
   *
   * The frame now renders at true A4 and is scaled to whatever the pane happens to be.
   *
   * ⚠ MEASURED, NOT ASSUMED. A hard-coded scale would be wrong the moment the layout changes or the
   * user zooms the browser, and it would be wrong silently.
   */
  const previewWrap = useRef<HTMLDivElement>(null);
  const [previewScale, setPreviewScale] = useState(1);
  useEffect(() => {
    const el = previewWrap.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const fit = () => setPreviewScale(el.clientWidth / 794);
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [previewHtml]);

  // ⚠ The preview is FETCHED, not linked.
  //
  // An <iframe src="/api/..."> cannot send the Authorization header — the browser issues that request
  // on its own, without the app's token — so the endpoint would return 401 and the panel would show a
  // blank frame with no explanation.
  //
  // Fetching it here and injecting via srcDoc keeps the auth header AND keeps the document's CSS
  // scoped to the frame, which matters because a rendered invoice carries its own stylesheet.
  const loadPreview = useCallback(async (templateId: string) => {
    try {
      setPreviewHtml(await api.getText(`/api/invoice-templates/${templateId}/preview`));
    } catch {
      // A failed preview is not worth an error banner — the design still saved, and the panel says so.
      setPreviewHtml(null);
    }
  }, []);

  /**
   * ═══ EVERY TEMPLATE ENTERS THROUGH HERE ═══
   *
   * ⚠ SIX PLACES SET A TEMPLATE and every one of them reads `.blocks` afterwards — a drag handler, a
   * filter, a length, three maps. Guarding each is six chances to miss one, and the seventh caller
   * added next year gets no guard at all.
   *
   * A response that is truthy but blockless is not hypothetical: `{}` is what this app receives from
   * an unstubbed call, a proxy error page, or a deploy where the endpoint moved. `if (template)`
   * passes on it and the designer dies on the first drag.
   *
   * ⚠ NULL FOR A NON-OBJECT, EMPTY BLOCKS FOR A SHAPELESS ONE. The two are different: no template
   * means "nothing selected" and the page says so; a template with no blocks is a blank sheet, which
   * is a real and drawable state.
   */
  const asTemplate = (t: unknown): Template | null => {
    if (!t || typeof t !== 'object' || !('id' in (t as object))) return null;
    const v = t as Template;
    return Array.isArray(v.blocks) ? v : { ...v, blocks: [] };
  };

  const load = useCallback(async () => {
    setError(null);
    try {
      const list = asList(await api.get<Template[]>('/api/invoice-templates'));
      setTemplates(list);
      if (list.length > 0) {
        const first = await api.get<Template>(`/api/invoice-templates/${list[0].id}`);
        setTemplate(asTemplate(first));
        void loadPreview(first.id);
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load templates.');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const refresh = (updated: Template) => {
    const safe = asTemplate(updated);
    setTemplate(safe);
    if (!safe) return;
    void loadPreview(safe.id);
    // Bumped so the preview iframe reloads. Reloading on the template object itself would refetch on
    // every render, not every change.
    setPreviewKey((k) => k + 1);
  };

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<Template>('/api/invoice-templates', {
        name: `Invoice design ${templates.length + 1}`,
      });
      await load();
      setTemplate(asTemplate(await api.get<Template>(`/api/invoice-templates/${created.id}`)));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not create a template.');
    } finally {
      setBusy(false);
    }
  };

  const addBlock = async (kind: string) => {
    if (!template) return;
    setError(null);
    try {
      // Dropped near the top-left, then dragged. Placing it centred would sit it on top of whatever
      // is already there, which is worse than a corner.
      refresh(await api.post<Template>(`/api/invoice-templates/${template.id}/blocks`, {
        kind, x: 14, y: 14, width: 60, height: 20,
      }));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not add that block.');
    }
  };

  const removeBlock = async (blockId: string) => {
    if (!template) return;
    setError(null);
    try {
      refresh(await api.del<Template>(`/api/invoice-templates/${template.id}/blocks/${blockId}`));
      setSelectedId(null);
    } catch (e) {
      // The domain refuses to delete a mandatory block, and its message explains which and why.
      setError(e instanceof ApiError ? e.message : 'Could not remove that block.');
    }
  };

  /**
   * Save a block's styling.
   *
   * The endpoint takes the WHOLE style record, not a patch — so every field is sent, with the
   * changed one overridden. Sending only the change would clear everything else, because the server
   * replaces rather than merges.
   */
  const styleBlock = async (block: Block, change: Partial<Block>) => {
    if (!template) return;
    setError(null);

    // Applied locally first so the canvas responds immediately. A round trip before the colour
    // changes makes the panel feel broken even when it is working.
    setTemplate((t) => t && {
      ...t,
      blocks: t.blocks.map((b) => (b.id === block.id ? { ...b, ...change } : b)),
    });

    try {
      const merged = { ...block, ...change };
      refresh(await api.put<Template>(
        `/api/invoice-templates/${template.id}/blocks/${block.id}/style`,
        {
          text: merged.text,
          fontFamily: merged.fontFamily,
          fontSizePt: merged.fontSizePt,
          bold: merged.bold,
          align: merged.align,
          colorHex: merged.colorHex,
          backgroundHex: merged.backgroundHex,
          borderHex: merged.borderHex,
          visible: merged.visible,
        },
      ));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save that change.');
      void load();   // reload rather than leave the canvas showing something that did not save
    }
  };

  /** The document's own accent colour and title — applies to the whole invoice, not one block. */
  const styleTemplate = async (change: { accentHex?: string | null; documentTitle?: string | null }) => {
    if (!template) return;
    setError(null);
    try {
      refresh(await api.put<Template>(`/api/invoice-templates/${template.id}/style`, {
        accentHex: change.accentHex !== undefined ? change.accentHex : template.accentHex,
        baseFontFamily: null,
        documentTitle: change.documentTitle !== undefined ? change.documentTitle : template.documentTitle,
        pageWidthMm: null,
        pageHeightMm: null,
        marginMm: null,
      }));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save that change.');
    }
  };

  const publish = async () => {
    if (!template) return;
    setBusy(true);
    setError(null);
    try {
      refresh(await api.post<Template>(`/api/invoice-templates/${template.id}/publish`, {}));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not publish this design.');
    } finally {
      setBusy(false);
    }
  };

  // ---- dragging ----
  //
  // Mouse events on the window, not the block: a fast drag outruns the element and the gesture would
  // otherwise die the moment the pointer leaves it.
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const drag = dragRef.current;
      const page = pageRef.current;
      if (!drag || !page || !template) return;

      // Screen pixels → page pixels → millimetres. Missing the zoom step is what makes a dragged
      // block outrun the pointer.
      const z = pageZoom(page);
      const dx = (e.clientX - drag.startX) / z / PX_PER_MM;
      const dy = (e.clientY - drag.startY) / z / PX_PER_MM;

      // Snapped to 1mm and clamped to the page, so a block cannot be dragged off the sheet where it
      // would silently vanish from the printed invoice.
      const x = Math.max(0, Math.min(PAGE_W - 10, Math.round(drag.blockX + dx)));
      const y = Math.max(0, Math.min(PAGE_H - 5, Math.round(drag.blockY + dy)));

      setTemplate((t) => t && {
        ...t,
        blocks: t.blocks.map((b) => (b.id === drag.id ? { ...b, x, y } : b)),
      });
    };

    const onUp = async () => {
      const drag = dragRef.current;
      dragRef.current = null;
      if (!drag || !template) return;

      // ⚠ Safe because every template enters through asTemplate — see the note on it.
      const block = template.blocks.find((b) => b.id === drag.id);
      if (!block) return;
      if (block.x === drag.blockX && block.y === drag.blockY) return;   // a click, not a drag

      try {
        // Saved once per gesture, not per mouse move — a request per frame would be hundreds of
        // writes for one drag.
        refresh(await api.put<Template>(
          `/api/invoice-templates/${template.id}/blocks/${block.id}/position`,
          { x: block.x, y: block.y, width: block.width, height: block.height },
        ));
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Could not save that position.');
        void load();
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [template, load]);

  const startDrag = (e: React.MouseEvent, block: Block) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedId(block.id);
    dragRef.current = {
      id: block.id, startX: e.clientX, startY: e.clientY,
      blockX: block.x, blockY: block.y,
    };
  };

  const selected = template?.blocks.find((b) => b.id === selectedId) ?? null;

  return (
    <div className="page-wrap">
      <header className="page-head">
        <div>
          <h1>Invoice designer</h1>
          <p className="muted">
            Your invoice, your way. Move anything, restyle anything — and what you already sent stays
            exactly as it was sent.
          </p>
        </div>

        <div className="head-actions">
          {templates.length > 0 && (
            <select
              className="input"
              value={template?.id ?? ''}
              onChange={async (e) => {
                setSelectedId(null);
                const picked = await api.get<Template>(`/api/invoice-templates/${e.target.value}`);
                setTemplate(asTemplate(picked));
                void loadPreview(picked.id);
              }}
            >
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}{t.isDefault ? ' (in use)' : ''}</option>
              ))}
            </select>
          )}
          <button className="btn" onClick={() => void create()} disabled={busy}>New design</button>
        </div>
      </header>

      {error && <div className="alert alert-error">{error}</div>}

      {!template && (
        <div className="empty-state">
          <h2>No invoice designs yet</h2>
          <p>Create one to start. It begins as a complete, working invoice you can rearrange.</p>
          <button className="btn btn-primary" onClick={() => void create()} disabled={busy}>
            Create a design
          </button>
        </div>
      )}

      {template && (
        <div className="designer">
          {/* ---- palette ---- */}
          <aside className="designer-palette">
            <h3>Add to the invoice</h3>
            {PALETTE.map((group) => (
              <div key={group.group} className="palette-group">
                <span className="palette-title">{group.group}</span>
                {group.items.map((item) => (
                  <button
                    key={item.kind}
                    className="palette-btn"
                    onClick={() => void addBlock(item.kind)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            ))}
          </aside>

          {/* ---- canvas ---- */}
          <div className="designer-canvas">
            <div
              ref={pageRef}
              className="invoice-page"
              style={{ width: PAGE_W * PX_PER_MM, height: PAGE_H * PX_PER_MM }}
              onMouseDown={(e) => { if (e.target === e.currentTarget) setSelectedId(null); }}
            >
              {template.blocks.filter((b) => b.visible).map((block) => (
                <div
                  key={block.id}
                  className={`invoice-block${block.id === selectedId ? ' is-selected' : ''}`}
                  style={{
                    left: block.x * PX_PER_MM,
                    top: block.y * PX_PER_MM,
                    width: block.width * PX_PER_MM,
                    height: block.height * PX_PER_MM,
                    zIndex: block.zOrder + 1,
                    // The block's own styling, so a colour change is visible on the canvas rather
                    // than only in the preview panel.
                    color: block.colorHex ?? undefined,
                    background: block.backgroundHex ?? undefined,
                    borderColor: block.borderHex ?? undefined,
                    fontWeight: block.bold ? 600 : undefined,
                    textAlign: (block.align as 'left' | 'center' | 'right' | undefined) ?? undefined,
                  }}
                  onMouseDown={(e) => startDrag(e, block)}
                >
                  <span className="invoice-block-label">
                    {KIND_LABEL[block.kind] ?? block.kind}
                  </span>
                  {block.isMandatory && (
                    <span className="invoice-block-lock" title="Required on every invoice">
                      required
                    </span>
                  )}
                </div>
              ))}
            </div>

            <p className="canvas-hint">
              Drag any block to move it. Positions snap to the millimetre.
            </p>
          </div>

          {/* ---- inspector + preview ---- */}
          <aside className="designer-side">
            <h3>{template.name}</h3>

            {template.problems.length > 0 ? (
              <div className="problems">
                <strong>Before this can be used:</strong>
                {template.problems.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            ) : (
              <p className="ready">Ready to use.</p>
            )}

            <button
              className="btn btn-primary full"
              onClick={() => void publish()}
              disabled={busy || template.problems.length > 0 || template.isDefault}
            >
              {template.isDefault ? 'Currently in use' : 'Use this design'}
            </button>

            {/* Document-wide settings. Separate from the block panel because they apply to the
                whole invoice — a school changing its accent colour expects every heading and rule to
                follow, not just whichever block happens to be selected. */}
            <h4>The whole invoice</h4>
            <div className="style-controls">
              <label className="style-row">
                <span>Accent</span>
                <select
                  className="input"
                  value={template.accentHex ?? ''}
                  onChange={(e) => void styleTemplate({ accentHex: e.target.value || null })}
                >
                  {COLOURS.map((c) => (
                    <option key={c.label} value={c.value ?? ''}>{c.label}</option>
                  ))}
                </select>
              </label>

              <label className="style-row style-row-stack">
                <span>Document title</span>
                <input
                  className="input"
                  defaultValue={template.documentTitle ?? ''}
                  placeholder="Fee Invoice"
                  onBlur={(e) => void styleTemplate({ documentTitle: e.target.value || null })}
                />
              </label>
            </div>

            {selected && (
              <div className="block-info">
                <h4>{KIND_LABEL[selected.kind] ?? selected.kind}</h4>
                <dl>
                  <div><dt>Position</dt><dd>{selected.x}, {selected.y} mm</dd></div>
                  <div><dt>Size</dt><dd>{selected.width} × {selected.height} mm</dd></div>
                </dl>

                <div className="style-controls">
                  <label className="style-row">
                    <span>Colour</span>
                    <select
                      className="input"
                      value={selected.colorHex ?? ''}
                      onChange={(e) => void styleBlock(selected, { colorHex: e.target.value || null })}
                    >
                      {COLOURS.map((c) => (
                        <option key={c.label} value={c.value ?? ''}>{c.label}</option>
                      ))}
                    </select>
                  </label>

                  <label className="style-row">
                    <span>Size</span>
                    <select
                      className="input"
                      value={selected.fontSizePt}
                      onChange={(e) => void styleBlock(selected, { fontSizePt: Number(e.target.value) })}
                    >
                      {FONT_SIZES.map((n) => <option key={n} value={n}>{n} pt</option>)}
                    </select>
                  </label>

                  <label className="style-row">
                    <span>Font</span>
                    <select
                      className="input"
                      value={selected.fontFamily ?? ''}
                      onChange={(e) => void styleBlock(selected, { fontFamily: e.target.value || null })}
                    >
                      {FONTS.map((f) => (
                        <option key={f.label} value={f.value ?? ''}>{f.label}</option>
                      ))}
                    </select>
                  </label>

                  <label className="style-row">
                    <span>Align</span>
                    <select
                      className="input"
                      value={selected.align ?? 'left'}
                      onChange={(e) => void styleBlock(selected, { align: e.target.value })}
                    >
                      <option value="left">Left</option>
                      <option value="center">Centre</option>
                      <option value="right">Right</option>
                    </select>
                  </label>

                  <label className="style-check">
                    <input
                      type="checkbox"
                      checked={selected.bold}
                      onChange={(e) => void styleBlock(selected, { bold: e.target.checked })}
                    />
                    Bold
                  </label>

                  {/* Free text is the only block whose wording a school writes itself. The others
                      draw their content from the invoice, and letting someone overwrite a total
                      with typed text would be a way to print a figure that is not the real one. */}
                  {(selected.kind === 'FreeText' || selected.kind === 'TermsText') && (
                    <label className="style-row style-row-stack">
                      <span>Wording</span>
                      <textarea
                        className="input"
                        rows={3}
                        defaultValue={selected.text ?? ''}
                        onBlur={(e) => void styleBlock(selected, { text: e.target.value })}
                      />
                    </label>
                  )}
                </div>

                {selected.isMandatory ? (
                  <p className="locked-note">
                    This must appear on every invoice. You can move it and restyle it, but not remove
                    it — an invoice without it would not be valid.
                  </p>
                ) : (
                  <button className="btn btn-danger full" onClick={() => void removeBlock(selected.id)}>
                    Remove this block
                  </button>
                )}
              </div>
            )}

            <h4>Preview</h4>
            {previewHtml ? (
              <div className="invoice-preview-wrap" ref={previewWrap}>
                <iframe
                  key={previewKey}
                  className="invoice-preview"
                  style={{ transform: `scale(${previewScale})` }}
                  srcDoc={previewHtml}
                  title="Invoice preview"
                  sandbox=""
                />
              </div>
            ) : (
              <p className="muted small">Preview will appear once the design has a few blocks.</p>
            )}
            <p className="muted small">
              Sample figures, not a real invoice.
            </p>
          </aside>
        </div>
      )}
    </div>
  );
}
